import React, { useEffect, useRef } from 'react';
import './SpiraBot.css';

/* The Welcome video: the bot wakes, then the camera pulls back. It plays
   when the card comes into view and stops at STOP_AT, while the bot is
   still close — the rest of the clip pulls out until it is tiny.
   Hovering the card plays it again. */
const STOP_AT = 2.5;

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function SpiraBot({ src }) {
  const rootRef = useRef(null);
  const videoRef = useRef(null);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };

  useEffect(() => {
    const el = rootRef.current;
    const v = videoRef.current;
    if (!el || !v) return undefined;

    // Hold on the stop frame instead of pulling all the way out.
    // Checked every frame while playing (timeupdate is only ~4/s and
    // would overshoot, then visibly jump back).
    let raf = 0;
    const watch = () => {
      if (v.currentTime >= STOP_AT) {
        v.pause();
        return;
      }
      raf = requestAnimationFrame(watch);
    };
    const onPlay = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(watch);
    };
    const onTime = () => cancelAnimationFrame(raf);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onTime);

    if (reducedMotion()) {
      const toStop = () => { v.currentTime = STOP_AT; };
      if (v.readyState >= 1) toStop();
      else v.addEventListener('loadedmetadata', toStop, { once: true });
      return () => {
        v.removeEventListener('play', onPlay);
        v.removeEventListener('pause', onTime);
      };
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) play();
        else v.pause();
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onTime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={rootRef}
      className="spira-bot"
      aria-hidden="true"
      onPointerEnter={() => { if (!reducedMotion()) play(); }}
    >
      <video ref={videoRef} src={src} muted playsInline preload="auto" />
    </div>
  );
}
